// biome-ignore-all lint/a11y: TODO - address a11y issues later
import { useEffect, useRef, useState } from "react";
import {
  ComposedChart,
  ReferenceArea,
  ReferenceLine,
  ResponsiveContainer,
  Scatter,
  XAxis,
  YAxis,
} from "recharts";
import { Skeleton } from "@/components/ui/skeleton.tsx";
import {
  type Person,
  useAccountPersons,
} from "@/hooks/api/use-account-persons";
import { PersonTooltip, type PersonTooltipProps } from "./chart-tooltips.tsx";
import type { VisibleTimeRange } from "./formatters.ts";
import { useFirstTouchpointsChartData } from "./hooks.ts";
import PersonAvatar from "./person-avatar.tsx";

interface ScatterPayload {
  payload?: {
    people: Person[];
    timestamp: number;
    id?: string;
    displayInitials?: string;
    cx?: number;
    cy?: number;
    x?: number;
    y?: number;
  };
}

interface FirstTouchpointsChartProps {
  accountId: string;
  visibleRange?: { start: string; end: string };
  visibleTimeRange: VisibleTimeRange | null;
  customTimeRange: { start: Date; end: Date } | null;
  domain: [number, number];
  ticks: number[];
  tickFormatter: (value: number) => string;
  onPersonClick?: (personIds: string) => void;
  onDateClick?: (timestamp: string) => void;
  isLoading: boolean;
}

export function FirstTouchpointsChart({
  accountId,
  visibleRange,
  visibleTimeRange,
  customTimeRange,
  domain,
  ticks,
  tickFormatter,
  onPersonClick,
  onDateClick,
  isLoading,
}: FirstTouchpointsChartProps) {
  const { getPersonById } = useAccountPersons(accountId);
  const containerRef = useRef<HTMLDivElement>(null);
  // Ref to store the timeout ID for proper cleanup
  const tooltipTimeoutRef = useRef<number | null>(null);
  const [containerHeight, setContainerHeight] = useState<number | undefined>(
    undefined,
  );

  const [tooltipData, setTooltipData] = useState<Pick<
    PersonTooltipProps,
    "people" | "timestamp"
  > | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerHeight(entry.contentRect.height);
      }
    });

    resizeObserver.observe(containerRef.current);

    setContainerHeight(containerRef.current.clientHeight);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const { formattedFirstTouchpoints } = useFirstTouchpointsChartData({
    visibleTimeRange,
    accountId,
    getPersonById,
    customTimeRange,
  });

  const handlePersonClick = (personIds: string, timestamp: string) => {
    if (onPersonClick) {
      onPersonClick(personIds);
    }
    if (onDateClick) {
      onDateClick(timestamp);
    }
  };

  const handleChartMouseLeave = () => {
    setTooltipData(null);
    setTooltipPosition(null);
  };

  const handleScatterMouseEnter = (
    payload: ScatterPayload["payload"],
    event: React.MouseEvent,
  ) => {
    if (payload?.people && payload.timestamp) {
      setTooltipData({ people: payload.people, timestamp: payload.timestamp });
      setTooltipPosition({ x: event.clientX, y: event.clientY });
    }
  };

  const handleScatterMouseLeave = () => {
    // Clear any existing timeout
    if (tooltipTimeoutRef.current !== null) {
      window.clearTimeout(tooltipTimeoutRef.current);
    }

    // Set a new timeout and store the ID
    tooltipTimeoutRef.current = window.setTimeout(() => {
      setTooltipData(null);
      setTooltipPosition(null);
      tooltipTimeoutRef.current = null; // Clear the ref when timeout completes
    }, 50);
  };

  // Clean up timeout when component unmounts
  useEffect(() => {
    return () => {
      if (tooltipTimeoutRef.current !== null) {
        window.clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  if (isLoading) {
    return <Skeleton className="h-full w-full" />;
  }

  return (
    <div className="relative w-full h-full">
      <div
        ref={containerRef}
        className="relative w-full h-full overflow-visible"
        onMouseLeave={handleChartMouseLeave}
      >
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={formattedFirstTouchpoints}
            margin={{ top: 0, right: 20, left: 25, bottom: -10 }}
          >
            <XAxis
              dataKey="timestamp"
              type="number"
              scale="time"
              domain={domain}
              ticks={ticks}
              interval={0}
              axisLine={false}
              tickLine={false}
              tick={({ x, y, payload }) => (
                <g transform={`translate(${x},${y})`}>
                  <text
                    x={0}
                    y={10}
                    textAnchor="middle"
                    fill="hsl(var(--muted-foreground))"
                    fontSize={12}
                  >
                    {tickFormatter(payload.value)}
                  </text>
                </g>
              )}
              height={30}
              pointerEvents="none"
            />
            <YAxis yAxisId="people" hide domain={[0, 1]} />
            {visibleRange && (
              <>
                <ReferenceLine
                  yAxisId="people"
                  x={Math.max(
                    domain[0],
                    new Date(visibleRange.start).getTime(),
                  )}
                  stroke="hsl(var(--chart-5))"
                  strokeWidth={0.5}
                  className="transition-all duration-200 ease-out"
                  pointerEvents="none"
                />
                <ReferenceLine
                  yAxisId="people"
                  x={Math.min(domain[1], new Date(visibleRange.end).getTime())}
                  stroke="hsl(var(--chart-5))"
                  strokeWidth={0.5}
                  className="transition-all duration-200 ease-out"
                  pointerEvents="none"
                />
                <ReferenceArea
                  yAxisId="people"
                  x1={Math.max(
                    domain[0],
                    new Date(visibleRange.start).getTime(),
                  )}
                  x2={Math.min(domain[1], new Date(visibleRange.end).getTime())}
                  fill="transparent"
                  stroke="transparent"
                  pointerEvents="none"
                  label={({ viewBox }) => {
                    const { x, y, width, height } = viewBox;
                    return (
                      <line
                        x1={x}
                        y1={y + height}
                        x2={x + width}
                        y2={y + height}
                        stroke="hsl(var(--chart-5))"
                        strokeWidth={0.5}
                        pointerEvents="none"
                      />
                    );
                  }}
                  className="transition-all duration-200 ease-out"
                />
              </>
            )}
            <Scatter
              yAxisId="people"
              dataKey="y"
              data={formattedFirstTouchpoints.map((d) => ({
                ...d,
                y: 0.5,
              }))}
              shape={
                <PersonAvatar
                  onClick={handlePersonClick}
                  containerHeight={containerHeight}
                  onMouseEnter={handleScatterMouseEnter}
                  onMouseLeave={handleScatterMouseLeave}
                />
              }
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <PersonTooltip
        people={tooltipData?.people || null}
        timestamp={tooltipData?.timestamp || null}
        position={tooltipPosition}
      />
    </div>
  );
}
